import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdatePaymentGateway from '@/modules/bank/api/hooks/useMutationUpdatePaymentGateway'
import useQueryGetPaymentGatewayById from '@/modules/bank/api/hooks/useQueryGetPaymentGatewayById'
import useQueryGetPaymentGatewayConstraints from '@/modules/bank/api/hooks/useQueryGetPaymentGatewayConstraints'
import Spinner from '@/common/components/Spinner'
import PaymentGatewayForm, {
	getChannelConfig,
} from '../../../PaymentGatewayForm'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectValue,
	SelectTrigger,
} from '@/common/ui/select'
import useQueryPaymentGatewayProvider from '@/modules/bank/api/hooks/useQueryPaymentGateProvider'
import { useEffect, useState } from 'react'
import { Label } from '@/common/ui/label'
import { BankQueryKeys } from '@/modules/bank/keys'
import { useQueryClient } from '@tanstack/react-query'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import { Checkbox } from '@/common/ui/checkbox'
import type { PaymentChannelTab } from '@/modules/bank/type'
import { UpdatePaymentGatewayRequestChannels } from '@/schema'
import { handlePaymentGatewayFormError } from '@/modules/bank/utils/handlePaymentGatewayFormError'

const CHANNEL_OPTIONS: { value: PaymentChannelTab; label: string }[] = [
	{ value: 'QR', label: 'QR' },
	{ value: 'TRANSFER', label: 'Transfer' },
	{ value: 'TRUE_MONEY', label: 'True Money' },
	{ value: 'P2P', label: 'P2P' },
]

export default function EditPaymentGatewayForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { mutate: updatePaymentGateway, isPending: isUpdating } =
		useMutationUpdatePaymentGateway()
	const { data: paymentGateway, isLoading } = useQueryGetPaymentGatewayById(id!)
	const { data: constraints, isLoading: isLoadingConstraints } =
		useQueryGetPaymentGatewayConstraints()
	const queryClient = useQueryClient()

	const [paymentGatewayProvider, setPaymentGatewayProvider] = useState<
		string | undefined
	>(paymentGateway?.bankProviderName)
	const [channels, setChannels] = useState<PaymentChannelTab[]>(['QR'])
	const [serverFieldErrors, setServerFieldErrors] = useState<
		Record<string, string>
	>({})

	const { data: paymentGateProviders } = useQueryPaymentGatewayProvider()

	// Sync channels from paymentGateway when loaded
	useEffect(() => {
		if (paymentGateway) {
			const ch: PaymentChannelTab[] = []
			if (paymentGateway.isChannelQr) ch.push('QR')
			if (paymentGateway.isChannelTransfer) ch.push('TRANSFER')
			if (paymentGateway.isChannelTrueMoney) ch.push('TRUE_MONEY')
			if ((paymentGateway as any).isChannelP2P) ch.push('P2P')
			setChannels(ch.length > 0 ? ch : ['QR'])
		}
	}, [paymentGateway])

	// Get provider-specific minimum deposit
	const getProviderMinDeposit = (provider: string): number => {
		if (!constraints) return 0
		const providerMin = (
			constraints.providerMinDeposits as Record<string, number>
		)?.[provider]
		return providerMin ?? constraints.defaultMinDeposit ?? 0
	}

	const onSubmit = (values: {
		displayName: string
		bankProviderName: string
		paymentGatewayId: string
		paymentGatewaySecretKey: string
		paymentGatewayPrivateKey: string
		paymentGatewayClientId: string
		paymentGatewayApiKey: string
		paymentGatewayAccountNumber: string
		senderName?: string
		deviceId?: string
		pin?: string
		paymentGatewayAccountName?: string
		paymentGatewayProviderName?: string
		minDepositAmount: number
		maxDepositAmount: number
		enableUploadSlip?: boolean
		uploadSlipDelaySeconds?: number
		isP2p?: boolean
		p2pStep?: number
	}) => {
		if (channels.length === 0) {
			toast.error('กรุณาเลือกอย่างน้อย 1 ช่องทาง')
			return
		}
		setServerFieldErrors({})
		const payload = {
			...values,
			id: id!,
			minDepositAmount: values.minDepositAmount,
			maxDepositAmount: values.maxDepositAmount,
			channels: channels as UpdatePaymentGatewayRequestChannels[],
			enableUploadSlip: values.enableUploadSlip,
			uploadSlipDelaySeconds: values.uploadSlipDelaySeconds,
			isP2p: values.isP2p,
			p2pStep: values.isP2p ? values.p2pStep : undefined,
		}
		updatePaymentGateway(
			{ payload },
			{
				onSuccess: () => {
					toast.success('แก้ไขบัญชีธนาคารออโต้สำเร็จ')
					navigate('/operator/payment-gateway')
					queryClient.invalidateQueries({
						queryKey: [BankQueryKeys.PaymentGatewayById, id],
					})
					queryClient.invalidateQueries({
						queryKey: [BankQueryKeys.ChannelCounts],
					})
				},
				onError: (error) => {
					const fieldErrors = handlePaymentGatewayFormError(error)
					if (fieldErrors) {
						setServerFieldErrors(fieldErrors)
						toast.error('รบกวนตรวจสอบข้อมูลอีกครั้ง')
					} else {
						showErrorToast(error)
					}
				},
			},
		)
	}

	useEffect(() => {
		if (paymentGateway) {
			setPaymentGatewayProvider(paymentGateway?.bankProviderName)
		}
	}, [paymentGateway])

	if (!paymentGateway || isLoading || isLoadingConstraints || !constraints)
		return <Spinner />

	return (
		<div className="mt-4 space-y-4">
			<LoadingOverlay open={isUpdating} message="กำลังอัพเดท ธนาคารออโต้..." />
			<Label>ผู้ให้บริการ</Label>
			<Select
				value={paymentGatewayProvider}
				onValueChange={setPaymentGatewayProvider}
				defaultValue={paymentGateway.bankProviderName}
				disabled={true}
			>
				<SelectTrigger className="h-8 w-[120px]">
					<SelectValue placeholder="ผู้ให้บริการ" />
				</SelectTrigger>
				<SelectContent>
					{paymentGateProviders?.map((paymentGateProvider) => (
						<SelectItem
							key={paymentGateProvider.name}
							value={paymentGateProvider.name}
							className="flex items-center gap-2"
						>
							{paymentGateProvider.name}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{paymentGatewayProvider && (
				<>
					<div className="space-y-2">
						<Label>ช่องทางที่รองรับ (อย่างน้อย 1 ช่องทาง)</Label>
						<div className="flex flex-wrap gap-4">
							{CHANNEL_OPTIONS.map((opt) => {
								const { notAvailableChannel } = getChannelConfig(
									paymentGatewayProvider ?? '',
								)
								const disabled = notAvailableChannel.includes(opt.value)
								return (
									<label
										key={opt.value}
										className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
									>
										<Checkbox
											checked={channels.includes(opt.value)}
											disabled={disabled}
											onCheckedChange={(checked) => {
												if (disabled) return
												if (checked) {
													setChannels((prev) => [...prev, opt.value])
												} else {
													setChannels((prev) =>
														prev.filter((c) => c !== opt.value),
													)
												}
											}}
										/>
										<span>{opt.label}</span>
									</label>
								)
							})}
						</div>
					</div>
					<PaymentGatewayForm
						paymentGatewayProvider={paymentGatewayProvider}
						mode="edit"
						onSubmit={onSubmit}
						constraints={{
							providerMinDeposit: getProviderMinDeposit(paymentGatewayProvider),
							globalMaxDeposit: constraints.globalMaxDeposit,
						}}
						serverFieldErrors={serverFieldErrors}
						defaultValues={{
							displayName: paymentGateway.displayName,
							paymentGatewayId: paymentGateway.paymentGatewayId,
							paymentGatewaySecretKey: paymentGateway.paymentGatewaySecretKey,
							paymentGatewayPrivateKey: paymentGateway.paymentGatewayPrivateKey,
							paymentGatewayClientId: paymentGateway.paymentGatewayClientId,
							paymentGatewayApiKey: paymentGateway.paymentGatewayApiKey,
							paymentGatewayAccountNumber:
								paymentGateway.paymentGatewayAccountNumber,
							senderName: paymentGateway.senderName,
							deviceId: paymentGateway.deviceId,
							pin: paymentGateway.pin,
							paymentGatewayAccountName:
								paymentGateway.paymentGatewayAccountName,
							paymentGatewayProviderName:
								paymentGateway.paymentGatewayProviderName,
							minDepositAmount: paymentGateway.minDepositAmount ?? undefined,
							maxDepositAmount: paymentGateway.maxDepositAmount ?? undefined,
							enableUploadSlip: paymentGateway.enableUploadSlip ?? false,
							uploadSlipDelaySeconds:
								paymentGateway.uploadSlipDelaySeconds ?? 60,
							isP2p: paymentGateway.isP2p ?? false,
							p2pStep: paymentGateway.p2pStep ?? 1,
							p2pThreshold: paymentGateway.p2pThreshold ?? undefined,
						}}
					/>
				</>
			)}
		</div>
	)
}
